import {
  DEFAULT_LINK_PREVIEW_ENDPOINT,
  DEFAULT_OEMBED_PROXY_ENDPOINT,
} from '@blocksuite/affine/shared/consts';
import {
  LinkPreviewCacheIdentifier,
  type LinkPreviewCacheProvider,
  LinkPreviewService,
  LinkPreviewServiceIdentifier,
} from '@blocksuite/affine/shared/services';
import { type ExtensionType } from '@blocksuite/affine/store';
import type { Container } from '@blocksuite/global/di';
import type { FrameworkProvider } from '@toeverything/infra';

import { ServerService } from '../../../modules/cloud/services/server';
import { WorkspaceService } from '../../../modules/workspace/services/workspace';
import { replacesDemoUserFiles } from '../../../modules/workspace/utils/demo-input-policy';

export class AffineLinkPreviewService extends LinkPreviewService {
  constructor(
    endpoint: string,
    oEmbedEndpoint: string,
    cache: LinkPreviewCacheProvider,
    workspaceId: string | null
  ) {
    super(cache);
    this.setEndpoint(endpoint);
    this.setOEmbedEndpoint(oEmbedEndpoint);
    if (workspaceId) {
      const query = this.query;
      const queryOEmbed = this.queryOEmbed;
      // The generic BlockSuite previewer also fetches Twitter directly in
      // the browser. Check the authoritative Demo policy before either path.
      // A failed policy lookup rejects before any external preview request.
      this.query = async (url, signal) =>
        (await replacesDemoUserFiles(workspaceId)) ? {} : query(url, signal);
      this.queryOEmbed = async (oEmbedUrl, signal) =>
        (await replacesDemoUserFiles(workspaceId))
          ? null
          : queryOEmbed(oEmbedUrl, signal);
    }
  }
}

/**
 * Patch the link preview service, set the endpoint and cache
 * @param framework
 * @returns
 */
export function patchLinkPreviewService(
  framework: FrameworkProvider
): ExtensionType {
  // get link preview service endpoint from server and BUILD_CONFIG
  let linkPreviewUrl: string;
  let oEmbedUrl: string;
  try {
    const server = framework.get(ServerService).server;
    linkPreviewUrl = new URL(
      BUILD_CONFIG.linkPreviewUrl || '/',
      server.baseUrl
    ).toString();
    oEmbedUrl = new URL(
      DEFAULT_OEMBED_PROXY_ENDPOINT,
      server.baseUrl
    ).toString();
  } catch (err) {
    console.error(
      'Invalid BUILD_CONFIG.linkPreviewUrl, falling back to default',
      err
    );
    linkPreviewUrl = DEFAULT_LINK_PREVIEW_ENDPOINT;
    oEmbedUrl = DEFAULT_OEMBED_PROXY_ENDPOINT;
  }

  return {
    setup: (di: Container) => {
      di.override(LinkPreviewServiceIdentifier, provider => {
        const workspace = framework.get(WorkspaceService).workspace;
        return new AffineLinkPreviewService(
          linkPreviewUrl,
          oEmbedUrl,
          provider.get(LinkPreviewCacheIdentifier),
          workspace.flavour === 'affine-cloud' ? workspace.id : null
        );
      });
    },
  };
}
